import React, { useEffect, useMemo, useRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";

import type { UndertakingEmailDraft, MailtoArgs } from "./types";

/** Dedupes by lowercase, drops empty/whitespace, keeps first-seen casing. */
function uniqueEmails(list: string[]): string[] {
    const result: string[] = [];
    const seen = new Set<string>();
    for (const raw of list) {
        const e = String(raw ?? "").trim();
        if (!e) continue;
        const key = e.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(e);
    }
    return result;
}

/** Builds mailto: URL with to, cc, bcc, subject, body as query params. */
function mailtoHref(args: MailtoArgs): string {
    const params = new URLSearchParams();
    if (args.cc && args.cc.length > 0) params.set("cc", args.cc.join(";"));
    if (args.bcc && args.bcc.length > 0)
        params.set("bcc", args.bcc.join(";"));
    if (args.subject.trim()) params.set("subject", args.subject);
    if (args.body.trim()) params.set("body", args.body);
    const q = params.toString();
    return `mailto:${String(args.to ?? "").trim()}${q ? `?${q}` : ""}`;
}

export default function UndertakingEmailModal({
    show,
    onHide,
    draft,
    setDraft,
}: {
    show: boolean;
    onHide: () => void;
    draft: UndertakingEmailDraft;
    setDraft: (next: UndertakingEmailDraft) => void;
}) {
    const editorRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!show) return;
        if (!editorRef.current) return;
        editorRef.current.innerText = String(draft.body ?? "");
    }, [show, draft.body]);

    const href = useMemo(() => {
        const toLower = String(draft.to ?? "").trim().toLowerCase();
        const ccList: string[] = [];
        if (draft.ccOtherSide) {
            ccList.push(String(draft.otherSideFirmContactEmail ?? "").trim());
        }
        const cc = uniqueEmails(ccList).filter(
            (e) => e.toLowerCase() !== toLower,
        );
        const bccList: string[] = [];
        if (draft.bccLawyer) bccList.push(String(draft.lawyerEmail ?? "").trim());
        if (draft.sendCopy) bccList.push(String(draft.copyTo ?? "").trim());
        const bcc = uniqueEmails(bccList).filter(
            (e) => e.toLowerCase() !== toLower,
        );
        return mailtoHref({
            to: draft.to,
            cc,
            bcc,
            subject: draft.subject,
            body: draft.body,
        });
    }, [draft]);

    const handleSend = () => {
        window.open(href, "_blank", "noopener,noreferrer");
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Email</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
                <div className="fw-semibold mb-2 small" style={{ color: "var(--txt-40)" }}>
                    {draft.contextLabel}
                </div>

                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                    {String(draft.copyTo ?? "").trim() && (
                        <Form.Check
                            type="switch"
                            id="undertaking-email-send-copy"
                            label="Send me a copy"
                            className="mb-0"
                            checked={draft.sendCopy}
                            onChange={(e) =>
                                setDraft({
                                    ...draft,
                                    sendCopy: e.target.checked,
                                })
                            }
                        />
                    )}
                    {String(draft.lawyerEmail ?? "").trim() && (
                        <Form.Check
                            type="switch"
                            id="undertaking-email-bcc-lawyer"
                            label="BCC Responsible Lawyer"
                            className="mb-0"
                            checked={draft.bccLawyer}
                            onChange={(e) =>
                                setDraft({
                                    ...draft,
                                    bccLawyer: e.target.checked,
                                })
                            }
                        />
                    )}
                    {String(draft.otherSideFirmContactEmail ?? "").trim() && (
                        <Form.Check
                            type="switch"
                            id="undertaking-email-cc-otherside"
                            label="CC Other Side Firm Contact"
                            className="mb-0"
                            checked={draft.ccOtherSide}
                            onChange={(e) =>
                                setDraft({
                                    ...draft,
                                    ccOtherSide: e.target.checked,
                                })
                            }
                        />
                    )}
                </div>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Subject</Form.Label>
                    <Form.Control
                        value={draft.subject}
                        onChange={(e) =>
                            setDraft({ ...draft, subject: e.target.value })
                        }
                        placeholder="Subject"
                    />
                </Form.Group>

                <div
                    className="rounded overflow-hidden border"
                    style={{
                        borderColor: "var(--txt-50)",
                        backgroundColor: "var(--bg-10)",
                        borderRadius: "var(--bdr-r-20)",
                    }}
                >
                    <div
                        ref={editorRef}
                        contentEditable
                        role="textbox"
                        aria-label="Email body"
                        style={{
                            minHeight: 220,
                            padding: "12px 12px 14px",
                            outline: "none",
                            whiteSpace: "pre-wrap",
                        }}
                        onInput={() => {
                            const el = editorRef.current;
                            if (!el) return;
                            setDraft({ ...draft, body: el.innerText });
                        }}
                        onBlur={() => {
                            const el = editorRef.current;
                            if (!el) return;
                            setDraft({ ...draft, body: el.innerText });
                        }}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-end gap-2 py-3 px-4">
                <Button variant="outline-primary" className="rounded-pill px-4" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    className="rounded-pill px-4"
                    onClick={handleSend}
                    disabled={!draft.to.trim()}
                >
                    Send
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
